import styles from './contract-history.module.scss';
import { IExistingOrderItemType, IExistingOrderType, OrderStateEnum, DeleteOrderDialog, SecondaryButton, ErrorButton, Autocomplete, TextField, Select, MenuItem } from '@frontend-workspace/react-shared-lib';
import { ValueFormatterParams } from 'ag-grid-community';
import MasterDetailTable from '../../master-detail-table/master-detail-table';
import { CreateHeaders, currencyHeader, dateHeader, HeaderSizeEnum, textHeader } from '../../table-helpers/table-helpers';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface ContractHistoryProps {
  orders: IExistingOrderType[],
  onDeleteOrder: (id: string) => void,
  onHideOrder?: (id: string) => void,
  onReasonChange: (id: string, reason: string) => void,
  reasonsForChange: { name: string }[]
}

export function ContractHistory(props: ContractHistoryProps) {
  const { t } = useTranslation();
  const [deleteOrder, setDeleteOrder] = useState<IExistingOrderItemType | undefined>(undefined);

  const openDeleteOrderDialog = (e: any) => setDeleteOrder(e.data);
  const closeDeleteOrderDialog = () => setDeleteOrder(undefined);

  const LicenseNumbersRenderer = (params: ValueFormatterParams) =>
    params.data.items.map((item: IExistingOrderItemType) => <div key={item.id}>
      {item.licenseNumber || ''}
    </div>);

  const ProductNameRenderer = (params: ValueFormatterParams) =>
    params.data.items.map((item: IExistingOrderItemType) => <div key={item.id}>
      {item.productName}
    </div>);

  const ChangeDescriptionRenderer = (params: ValueFormatterParams) =>
    params.data.items.map((item: IExistingOrderItemType) => <div key={item.id}>
      {item.changeDescription}
    </div>);

  const DeleteOrderRenderer = (params: ValueFormatterParams) => {
    return params.data.state === OrderStateEnum.Open ? <ErrorButton
      variant="outlined"
      size='small'
      onClick={() => openDeleteOrderDialog(params)}>
      {t("standard.delete")}
    </ErrorButton> : <ErrorButton
      variant="outlined"
      size='small'
      disabled>
      {t("standard.delete")}
    </ErrorButton>
  }
  const ReasonRenderer = (params: ValueFormatterParams) => {
    const handleChange = (e: any) => props.onReasonChange(params.data.id, e.target.value);

    return <Select
      name='evaluation'
      displayEmpty
      hiddenLabel
      fullWidth
      value={params.value || ''}
      onChange={handleChange}
      className='mt-1'
      sx={{ minWidth: 100 }}>
      {props.reasonsForChange.map((s) =>
        <MenuItem key={s.name} value={s.name}>
          {t(`reasonsForSale.${s.name}`)}
        </MenuItem>
      )}
    </Select>
  }
  const HideOrderRenderer = (params: ValueFormatterParams) => {
    const onHideOrder = props.onHideOrder;

    if (params.data.state === OrderStateEnum.Open && onHideOrder) {
      return <SecondaryButton
        variant="outlined"
        size='small'
        onClick={() => onHideOrder(params.value)}>
        {t("standard.hide")}
      </SecondaryButton>
    }
    if (params.data.state === OrderStateEnum.Failed && onHideOrder) {
      return <SecondaryButton
        variant="outlined"
        size='small'
        onClick={() => onHideOrder(params.value)}>
        {t("standard.show")}
      </SecondaryButton>
    }
    return <SecondaryButton
      variant="outlined"
      size='small'
      disabled>
      {t("standard.hide")}
    </SecondaryButton>
  }

  return (<>
    <MasterDetailTable
      masterHeaders={CreateHeaders([
        textHeader("changedBy", t("standard.changedBy"), HeaderSizeEnum.LARGE, { renderer: 'agGroupCellRenderer' }),
        dateHeader("activationDate", t("standard.executionDate")),
        dateHeader("orderDate", t("standard.orderDate")),
        textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.SMALL, { renderer: LicenseNumbersRenderer }),
        textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE, { renderer: ProductNameRenderer }),
        textHeader("change", t("contract.change"), HeaderSizeEnum.EXTRA_LARGE, { renderer: ChangeDescriptionRenderer }),
        currencyHeader("price", t("standard.price"), HeaderSizeEnum.MEDIUM, {
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("comment", t("standard.comment"), HeaderSizeEnum.MEDIUM),
        textHeader("id", "", HeaderSizeEnum.SMALL, {
          renderer: HideOrderRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("id", "", HeaderSizeEnum.SMALL, {
          renderer: DeleteOrderRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("reasonForSale", t("standard.reason"), HeaderSizeEnum.LARGE, {
          renderer: ReasonRenderer
        }),
      ])}
      data={props.orders}
      detailHeaders={CreateHeaders([
        textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("vismaContractItemId", "Visma ID", HeaderSizeEnum.SMALL),
        textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.SMALL, {
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        currencyHeader("discount", t("standard.discount"), HeaderSizeEnum.SMALL, {
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        currencyHeader("salePrice", t("standard.sum"), HeaderSizeEnum.SMALL, {
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        })
      ])}
      detailsPropertyName={"items"}
      fitHeight
    />
    {deleteOrder && <DeleteOrderDialog
      open={!!deleteOrder}
      onClose={closeDeleteOrderDialog}
      onDeleteOrder={props.onDeleteOrder}
      orderId={deleteOrder.id}
    />}
  </>);
}

export default ContractHistory;